import { useState } from 'react';
import { FaWhatsapp, FaCheckCircle } from 'react-icons/fa';
import axios from '../api/axios';

interface Props {
  collegeName: string;
  collegeCode: string;
}

const WHATSAPP_NUMBER = '918122552867';

const communities = ['OC', 'BC', 'BCM', 'MBC', 'SC', 'SCA', 'ST'];

const shortCollegeName = (name: string) =>
  name
    .replace(/\(Autonomous\)/gi, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/,\s*[A-Z][^,]*District[^,]*/gi, '')
    .replace(/,\s*\d{6}.*$/g, '')
    .trim();

interface FieldErrors {
  name?: string;
  phone?: string;
  score?: string;
  cutoff?: string;
}

const inputBase = 'w-full px-3 py-2.5 rounded-xl text-sm text-gray-900 bg-white placeholder-gray-400 focus:outline-none focus:ring-2';
const inputOk   = `${inputBase} focus:ring-white/50`;
const inputErr  = `${inputBase} ring-2 ring-red-400 focus:ring-red-400`;

const RequestInfoCard = ({ collegeName, collegeCode }: Props) => {
  const [name, setName]           = useState('');
  const [phone, setPhone]         = useState('');
  const [cutoff, setCutoff]       = useState('');
  const [rank, setRank]           = useState('');
  const [community, setCommunity] = useState('OC');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors]       = useState<FieldErrors>({});

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!name.trim())                        e.name   = 'Enter your name';
    if (!/^[6-9]\d{9}$/.test(phone.trim())) e.phone  = 'Enter a valid 10-digit number';
    if (!cutoff.trim() && !rank.trim())      e.score  = 'Enter at least your cutoff mark or rank';
    if (cutoff && (isNaN(Number(cutoff)) || Number(cutoff) < 0 || Number(cutoff) > 200))
                                             e.cutoff = 'Must be between 0 and 200';
    return e;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});

    axios.post('/leads', {
      name: name.trim(),
      phone: phone.trim(),
      cutoff_mark: cutoff || null,
      tnea_rank: rank || null,
      community,
      college_code: collegeCode,
      college_name: collegeName,
    }).catch(() => {});

    const parts: string[] = [];
    if (cutoff.trim()) parts.push(`Cutoff mark: ${cutoff} / 200`);
    if (rank.trim())   parts.push(`TNEA rank: ${Number(rank).toLocaleString()}`);
    const scoreLine = parts.join('  |  ') + `  (${community} community)`;

    const short = shortCollegeName(collegeName);
    const msg = `Hi, I need guidance for TNEA 2025 college selection.\n\nName: ${name.trim()}\n${scoreLine}\nI was looking at ${short} (${collegeCode}) — can you suggest the best colleges for me?\n\nPlease call me at ${phone.trim()}.`;

    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
        <FaCheckCircle className="text-green-500 text-4xl mx-auto mb-3" />
        <h3 className="font-bold text-green-800 text-lg mb-1">WhatsApp Opened!</h3>
        <p className="text-green-700 text-sm leading-relaxed">
          Send the pre-filled message — our counsellor will reply with college options matched to your rank.
        </p>
        <button
          onClick={() => { setSubmitted(false); setName(''); setPhone(''); setCutoff(''); setRank(''); setCommunity('OC'); setErrors({}); }}
          className="mt-4 text-xs text-green-600 underline hover:text-green-800"
        >
          Submit again
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-5 md:p-6 text-white">
      {/* Header */}
      <div className="mb-4">
        <p className="text-blue-200 text-xs font-semibold uppercase tracking-wider mb-1">Admission Enquiry</p>
        <h3 className="text-lg font-bold leading-snug">
          Interested in this college?
        </h3>
        <p className="text-blue-200 text-sm mt-1">
          Share your details — we'll check if you're eligible and connect you with the admissions team.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Row 1: Name + Phone */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <input
              type="text"
              placeholder="Your name"
              value={name}
              onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }}
              className={errors.name ? inputErr : inputOk}
            />
            {errors.name && <p className="text-red-300 text-[10px] mt-1 ml-1">{errors.name}</p>}
          </div>
          <div>
            <input
              type="tel"
              placeholder="Mobile number"
              value={phone}
              onChange={e => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setErrors(p => ({ ...p, phone: undefined })); }}
              className={errors.phone ? inputErr : inputOk}
            />
            {errors.phone && <p className="text-red-300 text-[10px] mt-1 ml-1">{errors.phone}</p>}
          </div>
        </div>

        {/* Row 2: Cutoff + Rank */}
        <div>
          {errors.score && (
            <p className="text-red-300 text-[10px] mb-1.5 ml-1">{errors.score}</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <input
                type="text"
                placeholder="Cutoff mark (0–200)"
                value={cutoff}
                onChange={e => { setCutoff(e.target.value); setErrors(p => ({ ...p, score: undefined, cutoff: undefined })); }}
                className={(errors.score || errors.cutoff) ? inputErr : inputOk}
              />
              {errors.cutoff
                ? <p className="text-red-300 text-[10px] mt-1 ml-1">{errors.cutoff}</p>
                : <p className="text-blue-300 text-[10px] mt-1 ml-1">Enter if you have it</p>
              }
            </div>
            <div>
              <input
                type="text"
                placeholder="TNEA rank"
                value={rank}
                onChange={e => { setRank(e.target.value.replace(/\D/g, '')); setErrors(p => ({ ...p, score: undefined })); }}
                className={errors.score ? inputErr : inputOk}
              />
              <p className="text-blue-300 text-[10px] mt-1 ml-1">Enter if you have it</p>
            </div>
          </div>
        </div>

        {/* Row 3: Community */}
        <div>
          <p className="text-blue-200 text-[11px] mb-1.5">Community</p>
          <div className="flex flex-wrap gap-2">
            {communities.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setCommunity(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  community === c
                    ? 'bg-white text-blue-700 border-white'
                    : 'bg-white/10 text-white border-white/30 hover:bg-white/20'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-400 active:bg-green-600 text-white font-bold py-3 rounded-xl transition-colors text-sm shadow-lg"
        >
          <FaWhatsapp className="text-lg" />
          Check My Chances →
        </button>
      </form>

      <p className="text-blue-300 text-[11px] text-center mt-3">
        No spam · No calls unless you ask · Replies within a few hours
      </p>
    </div>
  );
};

export default RequestInfoCard;
